import React, { useCallback, useEffect, useState } from 'react'
import { Box, Divider, Drawer, Typography } from '@mui/material'
import useAppUtilities from '../../hooks/useAppUtilities'
import ImgIcon from '../ui/ImgIcon'
import Talk from 'talkjs'
import { IconX } from '@tabler/icons-react'
import { useAppSelector } from '../../store'
import { selectRestaurantDetail } from '../../store/restaurantSlice'
import { Chatbox, useSession } from '@talkjs/react'
import { selectEater } from '../../store/profileSlice'
import {
  DEFAULT_CARD_IMAGE,
  toTalkPhotoUrl,
} from '../../helpers/static-constants'
import EmptyScreen from '../EmptyScreen'
import { Restaurant } from '../../types'

interface ChatDrawerProps {
  open: boolean
  toggleDrawer: React.Dispatch<React.SetStateAction<boolean>>
  restaurantInfo?: Restaurant | undefined
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({
  open,
  toggleDrawer,
  restaurantInfo,
}) => {
  const { isSmallScreen, theme } = useAppUtilities()
  const restaurantFromStore = useAppSelector(selectRestaurantDetail)?.restaurantDTO
  const restaurant = restaurantInfo ?? restaurantFromStore
  const eater = useAppSelector(selectEater)
  const session = useSession()
  const [isSession, setIsSession] = useState(false)

  useEffect(() => {
    if (!session?.isAlive) {
      setIsSession(true)
    }
  }, [session?.isAlive])

  const address = restaurant
    ? `${restaurant?.address1 ?? ''} ${restaurant?.city ?? ''}, ${
        restaurant?.state ?? ''
      }`
    : ''

  const syncConversation = useCallback(
    (session) => {
      const conversation = session.getOrCreateConversation(
        `Restaurant-${restaurant?.alias}-${eater?.eaterId}`,
      )

      const photoUrl = toTalkPhotoUrl(restaurant?.imageUrl, DEFAULT_CARD_IMAGE)
      const other = new Talk.User({
        id: restaurant?.restaurantId ?? '',
        name: restaurant?.name ?? '',
        email: restaurant?.contactEmail?.length
          ? restaurant?.contactEmail
          : 'test@test.com',
        ...(photoUrl ? { photoUrl } : {}),
        role: 'eater-restaurant-inbox',
        custom: {
          email: restaurant?.contactEmail ?? null,
          address: address.trim(),
        },
      })
      conversation.setParticipant(session.me)
      conversation.setParticipant(other)
      conversation.setAttributes({
        custom: {
          role: 'restaurant-eater-conversation',
        },
      })

      return conversation
    },
    [restaurant],
  )

  return (
    <Drawer
      open={open}
      onClose={() => toggleDrawer(false)}
      anchor={isSmallScreen ? 'bottom' : 'right'}
      sx={{
        '& .MuiPaper-root': {
          width: isSmallScreen ? '100%' : '400px',
          height: '100%',
        },
      }}
    >
      <Box
        sx={{
          pt: {
            xs: 'env(safe-area-inset-top)',
          },
          pb: {
            xs: 'env(safe-area-inset-bottom)',
          },
          height:
            'calc(100% - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
        }}
      >
        <Box>
          <Box sx={{ px: '16px', py: { xs: '16px', md: '30px' } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" color="text.primary" noWrap>
                {restaurant?.name}
              </Typography>
              <ImgIcon
                iconElement={<IconX stroke={2} style={{ margin: '7px' }} />}
                alt="close-drawer"
                wrapperStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: '50%',
                  height: '32px',
                  width: '32px',
                  cursor: 'pointer',
                }}
                onClick={() => toggleDrawer(false)}
              />
            </Box>
          </Box>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{ color: theme.palette.grey[300] }}
          />
        </Box>
        <Box
          sx={{
            bgcolor: 'background.paper',
            width: '100%',
            height: { xs: 'calc(100% - 5px)', md: 'calc(100% - 100px)' },
          }}
        >
          {!isSession ? (
            <Chatbox
              syncConversation={syncConversation}
              style={{ width: '100%', height: '100%' }}
              showChatHeader={false}
            ></Chatbox>
          ) : (
            <EmptyScreen
              mainText="Please wait while we initialize your chat session..."
              secondaryText="Session is loading..."
              imageUrl="/images/empty-screen.svg"
              containerHeight="400px"
            />
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

export default ChatDrawer
